import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  CardMedia,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EditIcon from "@mui/icons-material/Edit";

const initialContent = {
  heroImg: "https://images.unsplash.com/photo-1465101046530-73398c7f28ca",
  heroTitle: "Contact Studio Art",
  heroSubtitle: "Let’s create beautiful memories together!",
  studioImg: "https://images.unsplash.com/photo-1519125323398-675f0ddb6308",
  studioTitle: "Studio Art Management",
  studioAddress: "123, Main Road, Madurai, Tamil Nadu, India",
  studioPhone: "+91 98765 43210",
  studioEmail: "studioart@gmail.com",
  studioInstagram: "@studioartmadurai",
  studioWhatsapp: "+91 91234 56789",
  studioMap: "https://maps.googleapis.com/maps/api/staticmap?center=Madurai,Tamil+Nadu,India&zoom=13&size=400x120&maptype=roadmap&markers=color:red%7Clabel:S%7CMadurai,Tamil+Nadu,India",
  descriptionTitle: "Welcome to Studio Art!",
  descriptionText: "Our team is passionate about capturing your most precious moments. Whether it’s a wedding, event, or portrait session, we blend creativity and professionalism to deliver stunning results. Reach out to us for bookings, collaborations, or any queries. We’re always happy to help you create memories that last a lifetime!",
  contactFormTitle: "Send Us a Message",
  contactFormSubtitle: "Fill out the form and our team will get back to you soon!",
  contactSideImg: "https://images.unsplash.com/photo-1506744038136-46273834b3fb",
  galleryTitle: "Studio Gallery",
  gallerySubtitle: "Explore our creative work and happy clients!",
  gallery: [
    {
      img: "https://images.unsplash.com/photo-1465101046530-73398c7f28ca",
      title: "Wedding Moments",
      desc: "Capturing the joy and love of your special day.",
      color: "rgba(25,118,210,0.85)",
    },
    {
      img: "https://images.unsplash.com/photo-1519125323398-675f0ddb6308",
      title: "Our Creative Team",
      desc: "Meet the passionate professionals behind Studio Art.",
      color: "rgba(139,94,60,0.85)",
    },
    {
      img: "https://images.unsplash.com/photo-1506744038136-46273834b3fb",
      title: "Artistic Portraits",
      desc: "Stunning portraits that reflect your personality and style.",
      color: "rgba(92,64,50,0.85)",
    },
  ],
};

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [content, setContent] = useState(initialContent);
  const [editKey, setEditKey] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editImgFile, setEditImgFile] = useState(null);
  const [editGalleryIdx, setEditGalleryIdx] = useState(null);

  const clientMongoId = localStorage.getItem("clientMongoId");

  useEffect(() => {
    async function fetchContactPage() {
      if (!clientMongoId) return;
      try {
        const res = await axios.get(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/contact-page`);
        if (res.data.contactPage) setContent(res.data.contactPage);
      } catch (err) {
        setContent(initialContent);
      }
    }
    fetchContactPage();
  }, [clientMongoId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `https://studio-lc.onrender.com/api/clients/${clientMongoId}/contact-message`,
        form
      );
      alert("Thank you for contacting us!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      alert("Failed to send message.");
    }
  };

  // Open edit dialog for text/image
  const handleEdit = (key, value, idx = null) => {
    setEditKey(key);
    setEditValue(value);
    setEditImgFile(null);
    setEditGalleryIdx(idx);
  };

  const saveContactPageToDB = async (contactPage) => {
    try {
      await axios.put(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/contact-page`, { contactPage });
    } catch (err) {
      alert("Failed to save ContactPage content.");
    }
  };

  // Save changes
  const handleSave = async () => {
    let updated;
    if (editKey && editKey.endsWith("Img") && editImgFile) {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        if (editGalleryIdx !== null) {
          const updatedGallery = [...content.gallery];
          updatedGallery[editGalleryIdx].img = ev.target.result;
          updated = { ...content, gallery: updatedGallery };
        } else {
          updated = { ...content, [editKey]: ev.target.result };
        }
        setContent(updated);
        setEditKey(null);
        await saveContactPageToDB(updated);
      };
      reader.readAsDataURL(editImgFile);
      return;
    } else if (editGalleryIdx !== null) {
      const updatedGallery = [...content.gallery];
      updatedGallery[editGalleryIdx][editKey] = editValue;
      updated = { ...content, gallery: updatedGallery };
    } else {
      updated = { ...content, [editKey]: editValue };
    }
    setContent(updated);
    setEditKey(null);
    await saveContactPageToDB(updated);
  };

  return (
    <Box sx={{ bgcolor: "#f5faff", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          height: { xs: 220, md: 340 },
          mb: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <img
          src={content.heroImg}
          alt="Contact Hero"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "brightness(0.6)",
            position: "absolute",
            left: 0,
            top: 0,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            width: "100%",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontFamily: "'Playfair Display', serif",
              letterSpacing: 2,
              mb: 1,
              textShadow: "0 2px 8px #333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily:"Josefin Sans",
              gap: 1,
            }}
          >
            {content.heroTitle}
            <IconButton sx={{ color: "#fff" }} onClick={() => handleEdit("heroTitle", content.heroTitle)}>
              <EditIcon />
            </IconButton>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              textShadow: "0 2px 8px #333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily:"Jost",
              gap: 1,
              mt: 1,
            }}
          >
            {content.heroSubtitle}
            <IconButton sx={{ color: "#fff" }} onClick={() => handleEdit("heroSubtitle", content.heroSubtitle)}>
              <EditIcon />
            </IconButton>
          </Typography>
          <IconButton sx={{ position: "absolute", top: 16, right: 16, bgcolor: "#fff" }} onClick={() => handleEdit("heroImg", content.heroImg)}>
            <EditIcon />
          </IconButton>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Single Card with Studio Info and Description */}
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: 6,
            bgcolor: "#fff",
             maxWidth: "2200px", // increased width
             width: { xs: "100%", md: "1400px" },
            mx: "auto",
            mb: 6,
            ml: { xs: 0, md: -20 },
            p: { xs: 2, md: 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            gap: 4,
          }}
        >
          {/* Left: Studio Info */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ position: "relative" }}>
              <CardMedia
                component="img"
                image={content.studioImg}
                alt="Studio"
                sx={{
                  height: 180,
                  objectFit: "cover",
                  borderRadius: 2,
                  mb: 2,
                }}
              />
              <IconButton sx={{ position: "absolute", top: 8, right: 8, bgcolor: "#fff" }} onClick={() => handleEdit("studioImg", content.studioImg)}>
                <EditIcon />
              </IconButton>
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1976d2",
                fontFamily:"Josefin Sans",
                mb: 2,
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {content.studioTitle}
              <IconButton onClick={() => handleEdit("studioTitle", content.studioTitle)}>
                <EditIcon />
              </IconButton>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <LocationOnIcon sx={{ color: "#8b5e3c", mr: 1 }} />
              <Typography variant="body1" sx={{ color: "#5c4032", display: "inline-flex", alignItems: "center", gap: 1,fontFamily:"Jost", }}>
                {content.studioAddress}
                <IconButton onClick={() => handleEdit("studioAddress", content.studioAddress)}>
                  <EditIcon />
                </IconButton>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1,fontFamily:"Jost", }}>
              <PhoneIcon sx={{ color: "#8b5e3c", mr: 1 }} />
              <Typography variant="body1" sx={{ color: "#5c4032", display: "inline-flex", alignItems: "center", gap: 1,fontFamily:"Jost", }}>
                {content.studioPhone}
                <IconButton onClick={() => handleEdit("studioPhone", content.studioPhone)}>
                  <EditIcon />
                </IconButton>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1,fontFamily:"Jost", }}>
              <EmailIcon sx={{ color: "#8b5e3c", mr: 1 }} />
              <Typography variant="body1" sx={{ color: "#5c4032", display: "inline-flex", alignItems: "center", gap: 1,fontFamily:"Jost", }}>
                {content.studioEmail}
                <IconButton onClick={() => handleEdit("studioEmail", content.studioEmail)}>
                  <EditIcon />
                </IconButton>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <InstagramIcon sx={{ color: "#d32f2f", mr: 1 }} />
              <Typography variant="body1" sx={{ color: "#5c4032", display: "inline-flex", alignItems: "center", gap: 1,fontFamily:"Jost", }}>
                {content.studioInstagram}
                <IconButton onClick={() => handleEdit("studioInstagram", content.studioInstagram)}>
                  <EditIcon />
                </IconButton>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <WhatsAppIcon sx={{ color: "#25D366", mr: 1 }} />
              <Typography variant="body1" sx={{ color: "#5c4032", display: "inline-flex", alignItems: "center", gap: 1,fontFamily:"Jost", }}>
                {content.studioWhatsapp}
                <IconButton onClick={() => handleEdit("studioWhatsapp", content.studioWhatsapp)}>
                  <EditIcon />
                </IconButton>
              </Typography>
            </Box>
           
          </Box>
          {/* Right: Description */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#e7dfd5",
              borderRadius: 4,
              boxShadow: 2,
              p: 4,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
               fontFamily:"Josefin Sans",
                mb: 2,
                letterSpacing: 1,
                color: "#8b5e3c",
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {content.descriptionTitle}
              <IconButton onClick={() => handleEdit("descriptionTitle", content.descriptionTitle)}>
                <EditIcon />
              </IconButton>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "jost",
                fontSize: 18,
                lineHeight: 1.8,
                color: "#5c4032",
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {content.descriptionText}
              <IconButton onClick={() => handleEdit("descriptionText", content.descriptionText)}>
                <EditIcon />
              </IconButton>
            </Typography>
          </Box>
        </Card>

        {/* Contact Form with Side Image */}
        <Grid container spacing={6} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 4,
                bgcolor: "#e7dfd5",
                p: { xs: 2, md: 6 },
                height: "100%",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: 4,
                 width: { xs: "100%", md: "1380px" }, // increased width
                 maxWidth: "2200px", 
                 ml: { xs: 0, md: -20},
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#8b5e3c",
                    fontFamily:"Josefin Sans",
                    mb: 2,
                    letterSpacing: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {content.contactFormTitle}
                  <IconButton onClick={() => handleEdit("contactFormTitle", content.contactFormTitle)}>
                    <EditIcon />
                  </IconButton>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "jost",
                    color: "#5c4032",
                    mb: 3,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {content.contactFormSubtitle}
                  <IconButton onClick={() => handleEdit("contactFormSubtitle", content.contactFormSubtitle)}>
                    <EditIcon />
                  </IconButton>
                </Typography>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <TextField
                    label="Your Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Your Email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    fullWidth
                    type="email"
                  />
                  <TextField
                    label="Message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    fullWidth
                    multiline
                    minRows={4}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      bgcolor: "#1976d2",
                      color: "#fff",
                      fontWeight: 600,
                     fontFamily:"Josefin Sans",
                      mt: 2,
                      "&:hover": { bgcolor: "#1565c0" },
                    }}
                  >
                    Send Message
                  </Button>
                </Box>
              </Box>
              <Box sx={{ flex: 1, position: "relative", display: { xs: "none", md: "block" } }}>
                <img
                  src={content.contactSideImg}
                  alt="Contact Side"
                  style={{
                    width: "100%",
                    height: "320px",
                    objectFit: "cover",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(25,118,210,0.10)",
                  }}
                />
                <IconButton sx={{ position: "absolute", top: 8, right: 8, bgcolor: "#fff" }} onClick={() => handleEdit("contactSideImg", content.contactSideImg)}>
                  <EditIcon />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
          {/* Empty grid for spacing on large screens */}
          <Grid item xs={12} md={5}></Grid>
        </Grid>

        {/* Gallery Section */}
        <Box sx={{ mt: 20, textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontFamily:"Josefin Sans",
              color: "#1976d2",
              mb: 2,
              letterSpacing: 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {content.galleryTitle}
            <IconButton onClick={() => handleEdit("galleryTitle", content.galleryTitle)}>
              <EditIcon />
            </IconButton>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "jost",
              color: "#5c4032",
              mb: 3,
              fontSize: 18,
              maxWidth: 700,
              mx: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {content.gallerySubtitle}
            <IconButton onClick={() => handleEdit("gallerySubtitle", content.gallerySubtitle)}>
              <EditIcon />
            </IconButton>
          </Typography>
          <Grid container spacing={4} justifyContent="center" sx={{ mt: 3 }}>
            {content.gallery.map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: 6,
                    transition: "transform 0.3s",
                    "&:hover": { transform: "scale(1.04)", boxShadow: 12 },
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={item.img}
                      alt={item.title}
                      sx={{
                        filter: "brightness(0.85)",
                      }}
                    />
                    <IconButton sx={{ position: "absolute", top: 8, right: 8, bgcolor: "#fff" }} onClick={() => handleEdit("img", item.img, idx)}>
                      <EditIcon />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      bgcolor: item.color,
                      color: "#fff",
                      py: 2,
                      px: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 1,fontFamily:"Josefin Sans", }}>
                      {item.title}
                      <IconButton sx={{ color: "#fff" }} onClick={() => handleEdit("title", item.title, idx)}>
                        <EditIcon />
                      </IconButton>
                    </Typography>
                    <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", gap: 1 ,fontFamily:"Jost",}}>
                      {item.desc}
                      <IconButton sx={{ color: "#fff" }} onClick={() => handleEdit("desc", item.desc, idx)}>
                        <EditIcon />
                      </IconButton>
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* Edit Dialog */}
      <Dialog open={!!editKey} onClose={() => setEditKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit{" "}
          {editKey &&
            (editKey === "img"
              ? "Image"
              : editKey === "desc"
              ? "Description"
              : editKey === "title"
              ? "Title"
              : editKey.replace(/([A-Z])/g, " $1").replace("Img", " Image"))}
        </DialogTitle>
        <DialogContent>
          {editKey && (editKey.endsWith("Img") || editKey === "img") ? (
            <>
              <Box sx={{ mb: 2 }}>
                <img
                  src={editValue}
                  alt="Edit"
                  style={{
                    width: "100%",
                    maxWidth: 320,
                    height: 180,
                    objectFit: "cover",
                    borderRadius: 12,
                  }}
                />
              </Box>
              <Button variant="contained" component="label">
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => setEditImgFile(e.target.files[0])}
                />
              </Button>
            </>
) : (
  editKey === "studioMap" ? (
    <TextField
      label="Paste Google Maps embed or static map URL"
      fullWidth
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      sx={{ mt: 2 }}
    />
  ) : (
    <TextField
      multiline
      minRows={2}
      fullWidth
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      sx={{ mt: 2 }}
    />
  )
)}
        </DialogContent>
        <DialogActions>
          <Button sx={{fontFamily:"Josefin Sans",}} onClick={() => setEditKey(null)}>Cancel</Button>
          <Button sx={{fontFamily:"Josefin Sans",}} variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactPage;